// ModernDockSplitter — canvas と左/右 aside の境界に置く縦 drag-resize splitter.
//
// 背景 (docs/_wip/modern-layout.md §9.10 第3スライス、consult 2026-06-14 決定2):
//   - ドック幅をドラッグで変える手動編集 UI。WorkspaceState.widths[region] を更新する
//     (clamp [180,420] 既存に従う)。ModernSocialDock の縦 splitter (modernSocialDockResize)
//     と同流儀: pointer capture + リアルタイム更新 + 純関数で幅計算。
//   - 幾何計算 (pointer X → aside 幅) は workspaceLayout.dockWidthFromPointer の純関数に
//     切り出して単体テストに乗せる。本コンポーネントは DOM/pointer の配管のみ。
//   - 左 aside は canvas の左 = splitter は aside の右端 (右ドラッグで広がる)。
//     右 aside は canvas の右 = splitter は aside の左端 (左ドラッグで広がる)。
//
// engine/protocol には触れない。幅変更は onResize で親 (ModernLayout) の live workspace へ。

import { useCallback, useRef } from 'react';

import { DOCK_WIDTH_MAX, DOCK_WIDTH_MIN, dockWidthFromPointer } from './workspaceLayout.js';

export interface ModernDockSplitterProps {
  /** どちら側の aside の境界か (= 幅計算の符号が変わる)。 */
  readonly side: 'left' | 'right';
  /** 現在の aside 幅 px (= aria-valuenow 表示用)。 */
  readonly currentWidthPx: number;
  /**
   * 隣接 aside 要素への ref (= drag 開始時に境界 X を計測する anchor)。
   * 左 splitter は左 aside の右端、右 splitter は右 aside の左端を測る。
   */
  readonly asideRef: React.RefObject<HTMLElement | null>;
  /** drag 中リアルタイム / drag 終了時の確定で呼ぶ幅更新 (= live workspace へ流す)。 */
  readonly onResize: (widthPx: number) => void;
  /** aria ラベル (= どのドックの幅か)。 */
  readonly ariaLabel: string;
}

/**
 * 縦 splitter。canvas と aside の隙間 (flex gap) に重ねる薄い hit area。
 * pointer capture でリアルタイムに幅を更新する (ModernSocialDock と同パターン)。
 */
export const ModernDockSplitter = ({
  side,
  currentWidthPx,
  asideRef,
  onResize,
  ariaLabel,
}: ModernDockSplitterProps): React.ReactNode => {
  // drag 中の最新幅を保持 (= pointerup の確定永続で参照)。
  const latestWidthRef = useRef<number>(currentWidthPx);
  latestWidthRef.current = currentWidthPx;

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>): void => {
      const aside = asideRef.current;
      if (aside === null) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      const rect = aside.getBoundingClientRect();
      // 左 aside の右端 / 右 aside の左端を anchor に。drag 中は固定。
      const asideEdgeX = side === 'left' ? rect.left : rect.right;
      const fallback = currentWidthPx;
      const move = (ev: PointerEvent): void => {
        const next = dockWidthFromPointer(side, ev.clientX, asideEdgeX, fallback);
        latestWidthRef.current = next;
        onResize(next);
      };
      const up = (ev: PointerEvent): void => {
        try {
          (ev.target as Element | null)?.releasePointerCapture?.(ev.pointerId);
        } catch {
          /* defensive */
        }
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        // 確定 (= 最終値を改めて流す。永続化は親が onResize 内で行う)。
        onResize(latestWidthRef.current);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    },
    [asideRef, side, currentWidthPx, onResize],
  );

  return (
    // biome-ignore lint/a11y/useSemanticElements: drag-resize splitter は role="separator" 慣用 (hairline 子要素を含むため div).
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={Math.round(currentWidthPx)}
      aria-valuemin={DOCK_WIDTH_MIN}
      aria-valuemax={DOCK_WIDTH_MAX}
      tabIndex={0}
      data-testid={`modern-dock-splitter-${side}`}
      className="group flex w-[6px] shrink-0 cursor-ew-resize select-none items-stretch justify-center"
      style={{ touchAction: 'none' }}
      onPointerDown={onPointerDown}
    >
      {/* hover/active で薄いアクセント線 (hit area 中央の 2px bar)。 */}
      <div className="w-[2px] rounded-full bg-[var(--color-border)] transition-colors group-hover:bg-[var(--color-accent)] group-active:bg-[var(--color-accent)]" />
    </div>
  );
};
